import { Children, cloneElement, type HTMLAttributes, isValidElement, type ReactNode } from 'react';

/**
 * Merges Slot props onto the child: `on*` handlers composed (both fire), className concatenated,
 * style shallow-merged (child wins); all other props the child wins.
 */
function mergeProps(slotProps: Record<string, unknown>, childProps: Record<string, unknown>) {
  const merged: Record<string, unknown> = { ...slotProps };

  for (const key of Object.keys(childProps)) {
    const slotVal = slotProps[key];
    const childVal = childProps[key];

    if (key === 'className') {
      merged[key] = [slotVal, childVal].filter(Boolean).join(' ');
    } else if (key === 'style' && typeof slotVal === 'object' && typeof childVal === 'object') {
      merged[key] = { ...(slotVal as object), ...(childVal as object) };
    } else if (/^on[A-Z]/.test(key) && typeof slotVal === 'function' && typeof childVal === 'function') {
      merged[key] = (...args: unknown[]) => {
        (childVal as (...a: unknown[]) => unknown)(...args);
        (slotVal as (...a: unknown[]) => unknown)(...args);
      };
    } else {
      merged[key] = childVal;
    }
  }

  return merged;
}

export function Slot({ children, ...slotProps }: HTMLAttributes<HTMLElement> & { children?: ReactNode }) {
  if (isValidElement(children)) {
    return cloneElement(children, mergeProps(slotProps, children.props as Record<string, unknown>));
  }

  if (Children.count(children) > 1) {
    Children.only(null); // throws
  }

  return null;
}
